import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, Github, ExternalLink, Search } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Line = ({ label, value, testid }) => (
  <div className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0">
    <span className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A] w-[150px] shrink-0">{label}</span>
    <span className="text-[12.5px] text-[#1A1A1A] break-all" data-testid={testid}>{value}</span>
  </div>
);

const Hit = ({ h }) => (
  <div className="border-b border-[#F4F2ED] pb-2 last:border-0" data-testid={`github-hit-${h.path}`}>
    <a href={h.url} target="_blank" rel="noopener noreferrer"
       className="text-[12.5px] font-semibold text-[#1A1A1A] underline inline-flex items-center gap-1 break-all">
      {h.repo?.split("/").pop()}/{h.path} <ExternalLink className="w-3 h-3 shrink-0" />
    </a>
    <p className="text-[11.5px] text-[#8A857A] mt-0.5">{h.relevance} · {h.match_reason}</p>
    {(h.fragments || []).slice(0, 2).map((f, i) => (
      <pre key={i} className="mt-1 text-[11.5px] text-[#59554D] bg-[#F7F5F0] rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">{f}</pre>
    ))}
  </div>
);

/* GitHub is connected once for the whole company, not per agent: support staff get
   code evidence without GitHub accounts of their own.

   The code is read live with a read-only token — nothing is copied here — so what
   an admin controls is which repositories triage may read, since support tickets
   only ever concern a few of them. */
export default function GithubConnectionCard() {
  const [h, setH] = useState(null);
  const [repos, setRepos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/github/health`);
      setH(r.data);
      if (r.data?.connected) {
        try {
          const rr = await axios.get(`${API}/github/repos`);
          setRepos(rr.data);
        } catch { setRepos(null); }
      }
    } catch (e) {
      setH({ configured: false, connected: false, detail: e?.response?.data?.detail || "Health check failed" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRepo = async (full) => {
    const current = repos?.scope?.length ? repos.scope : [];
    const next = current.includes(full) ? current.filter((x) => x !== full) : [...current, full];
    setBusy(true);
    try {
      const r = await axios.put(`${API}/github/scope`, { repos: next });
      setRepos((p) => ({ ...p, scope: r.data.scope, allowed: r.data.allowed }));
      toast.success(r.data.scope.length
        ? `Ask The Code may read ${r.data.scope.length} repository(ies)`
        : "No repositories approved — code reading is now off");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the repository list");
    } finally { setBusy(false); }
  };

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true); setResult(null);
    try {
      const r = await axios.post(`${API}/github/search`, { query: query.trim() });
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "The search failed" });
    } finally { setSearching(false); }
  };

  if (loading) {
    return (
      <Card className="border-[#DAD5CA]"><CardContent className="p-5">
        <Loader2 className="w-4 h-4 animate-spin text-[#8A857A]" data-testid="github-loading" />
      </CardContent></Card>
    );
  }

  const ok = h?.ready;
  const readable = repos?.repos || [];
  const scope = repos?.scope || [];
  const allowed = repos?.allowed || [];

  return (
    <Card className="border-[#DAD5CA]" data-testid="github-connection-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <Github className="w-4 h-4 mt-0.5 text-[#1A1A1A]" />
            <div>
              <h3 className="text-[15px] font-bold text-[#1A1A1A]">GitHub</h3>
              <p className="text-[12.5px] text-[#59554D] mt-0.5 max-w-[520px]">
                Read-only code evidence for triage, read live from GitHub — no copy of the
                code is kept here. Connected once for the company; agents do not need
                GitHub accounts.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
                  className="border-[#DAD5CA]" data-testid="github-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className={`mt-4 rounded-lg border p-3 ${ok ? "border-[#B9CBB4] bg-[#F3F7F1]" : "border-[#E5C98A] bg-[#FDF8EC]"}`}>
          <p className="text-[12.5px] font-semibold inline-flex items-center gap-1.5" data-testid="github-status">
            {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-[#3F6B35]" /> : <AlertTriangle className="w-3.5 h-3.5 text-[#8A6D00]" />}
            {ok
              ? `Reading ${h.repo_count} repository(ies)${h.account ? ` as ${h.account}` : ""}`
              : !h?.configured ? "No GitHub token set"
                : !h?.connected ? "The token was rejected"
                  : "Connected, but no repositories are readable"}
          </p>
          {h?.detail && <p className="text-[12.5px] text-[#59554D] mt-1 break-words" data-testid="github-detail">{h.detail}</p>}
        </div>

        <div className="mt-4">
          <Line label="Account" value={h?.account || "—"} testid="github-account" />
          <Line label="Readable repos" value={h?.repo_count || "—"} testid="github-installed-count" />
          <Line label="Private repos" value={h?.private_count ?? "—"} testid="github-private-count" />
          <Line label="Used in triage" value={allowed.length ? `${allowed.length} approved` : "none approved"} testid="github-allowed-count" />
          <Line label="Searches left this minute" value={`${h?.searches_left ?? 0} of 10`} testid="github-search-budget" />
        </div>

        {readable.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">
              Repositories Ask The Code may read
            </p>
            <p className="text-[12px] text-[#59554D] mt-0.5">
              An allow-list: only ticked repositories are searched or read, by Ask The Code
              and by ticket triage. A repository added to the account later stays unreadable
              until it is ticked here.
            </p>
            {scope.length === 0 ? (
              <p className="text-[12.5px] text-[#8A6D00] bg-[#FDF8EC] border border-[#E5C98A] rounded px-2.5 py-1.5 mt-2"
                 data-testid="github-scope-empty">
                Nothing is ticked, so Ask The Code reads nothing and says so when asked.
              </p>
            ) : (
              <p className="text-[12px] text-[#3F6B35] mt-1.5" data-testid="github-scope-count">
                {scope.length} of {readable.length} approved.
              </p>
            )}
            <div className="mt-2 max-h-[240px] overflow-y-auto rounded-lg border border-[#DAD5CA]">
              {readable.map((r) => {
                const on = scope.includes(r.full_name);
                return (
                  <label key={r.full_name}
                         className="flex items-center gap-2 px-3 py-1.5 border-b border-[#F4F2ED] last:border-0 cursor-pointer hover:bg-[#FBFAF7]">
                    <input type="checkbox" checked={scope.includes(r.full_name)} disabled={busy}
                           onChange={() => toggleRepo(r.full_name)}
                           data-testid={`github-repo-${r.full_name}`} />
                    <span className={`text-[12.5px] ${on ? "text-[#1A1A1A]" : "text-[#8A857A]"} truncate`}>
                      {r.full_name.split("/").pop()}
                    </span>
                    <span className="text-[11.5px] text-[#8A857A] ml-auto shrink-0">
                      {[r.private ? "private" : "public", r.pushed_at].filter(Boolean).join(" · ")}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {h?.connected && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A]">Test a code search</p>
            <div className="flex gap-2 mt-1.5">
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && search()}
                     placeholder="an error string, function or class name"
                     className="border-[#DAD5CA] text-[13px]" data-testid="github-query-input" />
              <Button onClick={search} disabled={searching || !query.trim()}
                      className="bg-[#1A1A1A] text-white" data-testid="github-query-submit">
                {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </Button>
            </div>

            {result && !result.ok && (
              <div className="mt-3 rounded-lg border border-[#E8A5A0] bg-[#FDF0EF] p-3" data-testid="github-query-error">
                <p className="text-[12.5px] font-semibold text-[#8C2F28]">That search could not run</p>
                <p className="text-[12.5px] text-[#8C2F28] mt-1 break-words">{result.error}</p>
                {result.hint && <p className="text-[12px] text-[#59554D] mt-2">{result.hint}</p>}
              </div>
            )}
            {result?.ok && (
              <div className="mt-3 rounded-lg border border-[#DAD5CA] bg-[#FBFAF7] p-3" data-testid="github-query-result">
                <p className="text-[12px] text-[#8A857A]">
                  {result.total} file(s) matched across {(result.searched_repos || []).length} repository(ies)
                </p>
                <div className="mt-2 space-y-2">
                  {(result.hits || []).length === 0
                    ? <p className="text-[12.5px] text-[#59554D]" data-testid="github-query-empty">
                        That text appears in none of them — note GitHub searches the default
                        branch only, so an unmerged change will not show.
                      </p>
                    : result.hits.map((x) => <Hit key={`${x.repo}/${x.path}`} h={x} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
